import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabase/config';
import { checkInService } from '../../services/checkInService';
import {
    Award,
    History,
    User,
    TrendingUp,
    Calendar,
    Phone,
    Mail,
    ChevronRight,
    Star,
    ArrowLeft,
    Tag,
    Bell,
    Gift,
    Ticket,
    CheckCircle2,
    XCircle,
    Copy,
    Sparkles,
    Hash,
    Coffee,
    Cake,
    Utensils,
    CreditCard,
    QrCode,
    ShoppingCart,
    ShoppingBag,
    Minus,
    Plus,
    Trash2,
    Lock,
    Instagram,
    MapPin
} from 'lucide-react';
import DigitalMenu from '../../components/DigitalMenu';
import PWAInstallPrompt from '../../components/PWAInstallPrompt';
import PremiumDatePicker from '../../components/PremiumDatePicker';
import CustomerPortalCart from '../../components/portal/CustomerPortalCart';
import CustomerPortalRewards from '../../components/portal/CustomerPortalRewards';
import CustomerPortalPromotions from '../../components/portal/CustomerPortalPromotions';
import CustomerPortalProfile from '../../components/portal/CustomerPortalProfile';
import CustomerPortalNotifications from '../../components/portal/CustomerPortalNotifications';
import { requestNotificationPermission } from '../../utils/pushNotifications';
import { QRCodeSVG } from 'qrcode.react';
import { generateShortId } from '../../utils/customer';
import { calculateLoyaltyLevel, calculateVisitFrequencyRole, defaultLoyaltyLevels } from '../../utils/loyalty';
import { useAlert } from '../../context/AlertContext';

const CustomerPortal = () => {
    const { tenantId, customerId: rawCustomerId } = useParams();
    const [customerId, setCustomerId] = useState(() => {
        if (rawCustomerId) return rawCustomerId;
        // Will be resolved from DB in fetchData using global identity
        return null;
    });
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [tenant, setTenant] = useState(null);
    const [actualTenantId, setActualTenantId] = useState(null);
    const [inventory, setInventory] = useState({});
    const [customer, setCustomer] = useState(null);
    const [history, setHistory] = useState([]);
    const [discounts, setDiscounts] = useState([]);
    const [redemptions, setRedemptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null); // 'tenant_not_found' or 'customer_not_found'
    const [activeTab, setActiveTab] = useState(() => {
        const saved = localStorage.getItem(`lastTab_${customerId}`);
        return saved || 'menu';
    });
    const [couponSubTab, setCouponSubTab] = useState('active'); // active, passive
    const [redeeming, setRedeeming] = useState(null); // stores discount being redeemed
    const [showCode, setShowCode] = useState(null); // stores the generated code to show
    const [notifications, setNotifications] = useState([]);
    const [menu, setMenu] = useState(null);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [readNotifications, setReadNotifications] = useState(() => {
        const saved = localStorage.getItem(`read_notifs_${customerId}`);
        return saved ? JSON.parse(saved) : [];
    });
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [foregroundNotification, setForegroundNotification] = useState(null);
    const [cart, setCart] = useState(() => {
        const saved = localStorage.getItem(`cart_${customerId}`);
        return saved ? JSON.parse(saved) : [];
    });
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [tableNumber, setTableNumber] = useState(() => {
        const urlTable = new URLSearchParams(window.location.search).get('table');
        if (urlTable) return urlTable;
        // No table param = store QR → default to Gel Al
        return 'GelAl';
    });
    const [appliedPromotion, setAppliedPromotion] = useState(() => {
        const saved = localStorage.getItem(`promo_${customerId}`);
        return saved ? JSON.parse(saved) : null;
    });
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [urlTableParam] = useState(() => new URLSearchParams(window.location.search).get('table'));

    // Persist table number from URL to localStorage
    useEffect(() => {
        if (tableNumber && customerId) {
            localStorage.setItem(`tableNum_${customerId}`, tableNumber);
        }
    }, [tableNumber, customerId]);

    const [rewardCode, setRewardCode] = useState('');
    const [addedCoupons, setAddedCouponsRaw] = useState([]);
    const customerIdRef = React.useRef(customerId);
    React.useEffect(() => { customerIdRef.current = customerId; }, [customerId]);
    const setAddedCoupons = React.useCallback((val) => {
        setAddedCouponsRaw(prev => {
            const next = typeof val === 'function' ? val(prev) : val;
            // DB'ye kupon kodlarını kaydet
            const codes = next.map(c => c.code).filter(Boolean);
            const cid = customerIdRef.current;
            if (cid) {
                supabase.from('customers').update({ saved_coupons: codes }).eq('id', cid).then(({ error }) => {
                    if (error) console.error('saved_coupons update error:', error);
                });
            }
            return next;
        });
    }, []);
    const [appliedReward, setAppliedReward] = useState(null); // { interaction, discount }
    const [activeRewardCodes, setActiveRewardCodes] = useState([]); // Now fetched from DB
    const [checkoutModal, setCheckoutModal] = useState(null); // { html?, iframeUrl?, token, provider } for payment form

    // Whether customers can place orders directly from the portal menu. Requires
    // a paid plan AND the business not having disabled ordering in settings.
    // When off, the portal behaves like the Free plan: menu-only (no cart, no
    // "add to cart", no checkout).
    const hasPaidPlan = tenant?.plan === 'pro' || tenant?.plan === 'premium';
    const orderingEnabled = hasPaidPlan && tenant?.theme?.portalOrderingEnabled !== false;

    // If ordering is disabled while the cart tab is somehow active (restored
    // from saved tab or ?tab=cart), fall back to the menu once tenant is loaded.
    useEffect(() => {
        if (tenant && !orderingEnabled && activeTab === 'cart') {
            setActiveTab('menu');
        }
    }, [tenant, orderingEnabled, activeTab]);

    const isPromotionEligible = (promo, cart, customer, redemptions) => {
        if (!promo || !cart || cart.length === 0) return { eligible: false, reason: 'Sepet boş' };

        // 1. Basic Validity (Roles, Limits, Dates, Days)
        if (!promo.isActive) return { eligible: false, reason: 'Bu kampanya artık aktif değil.' };

        const now = new Date();
        // Day of week check
        const today = now.getDay();
        const yesterday = (today + 6) % 7;
        const currentTotalMinutes = now.getHours() * 60 + now.getMinutes();

        if (promo.startTime && promo.expiryTime) {
            const [startH, startM] = promo.startTime.split(':').map(Number);
            const [endH, endM] = promo.expiryTime.split(':').map(Number);
            const startTotalMinutes = startH * 60 + startM;
            const endTotalMinutes = endH * 60 + endM;

            const isTodayApplicable = !promo.applicableDays || !promo.applicableDays.length || promo.applicableDays.includes(today);
            const isYesterdayApplicable = !promo.applicableDays || !promo.applicableDays.length || promo.applicableDays.includes(yesterday);

            let activeNow = false;

            // Check Today's Window
            if (isTodayApplicable) {
                if (startTotalMinutes <= endTotalMinutes) {
                    // Normal case
                    if (currentTotalMinutes >= startTotalMinutes && currentTotalMinutes <= endTotalMinutes) activeNow = true;
                } else {
                    // Overnight - Today's Part (Tonight starting at startTime until midnight)
                    if (currentTotalMinutes >= startTotalMinutes) activeNow = true;
                }
            }

            // Check Yesterday's Window (Midnight crossover part)
            if (!activeNow && isYesterdayApplicable) {
                if (startTotalMinutes > endTotalMinutes) {
                    if (currentTotalMinutes <= endTotalMinutes) activeNow = true;
                }
            }

            if (!activeNow) {
                return { eligible: false, reason: `Kampanya şu an geçerli değil. Geçerli saatler: ${promo.startTime} - ${promo.expiryTime}` };
            }
        } else if (promo.applicableDays && Array.isArray(promo.applicableDays) && promo.applicableDays.length > 0) {
            // No time restriction, just day check
            if (!promo.applicableDays.includes(today)) {
                return { eligible: false, reason: 'Bu kampanya bugün geçerli değil.' };
            }
        }

        // Overall Campaign Date Check
        if (promo.startDate) {
            const sDate = new Date(promo.startDate);
            if (new Date() < sDate) return { eligible: false, reason: 'Kampanya henüz başlamadı.' };
        }
        if (promo.expiryDate) {
            const eDate = new Date(promo.expiryDate);
            // End of the day for expiryDate comparison
            eDate.setHours(23, 59, 59, 999);
            if (new Date() > eDate) return { eligible: false, reason: 'Kampanya sona erdi.' };
        }


        // Target filtering
        let targetMatch = false;
        if (promo.target === 'all') targetMatch = true;
        else if (promo.target === 'groups') {
            const allowedLevels = Array.isArray(promo.loyaltyStatus) ? promo.loyaltyStatus : (promo.loyaltyStatus ? [promo.loyaltyStatus] : []);
            const allowedRoles = Array.isArray(promo.visitRole) ? promo.visitRole : (promo.visitRole ? [promo.visitRole] : []);
            if (allowedLevels.includes(customer.loyaltyLevel) || allowedRoles.includes(customer.visitRole)) targetMatch = true;
        }
        else if (promo.target === 'visit_role') {
            const allowed = Array.isArray(promo.visitRole) ? promo.visitRole : (promo.visitRole ? [promo.visitRole] : []);
            if (allowed.includes(customer.visitRole)) targetMatch = true;
        }
        else if (promo.target === 'customers' && promo.selectedCustomers?.includes(customerId)) targetMatch = true;

        if (!targetMatch) return { eligible: false, reason: 'Bu kampanya sınıfa/role özeldir.' };

        // Total Limit check
        if (promo.totalLimit && (promo.usageCount || 0) >= promo.totalLimit) {
            return { eligible: false, reason: 'Kampanya toplam kullanım sınırına ulaştı.' };
        }

        // Customer limit check
        if (promo.customerLimit) {
            const usedCount = (redemptions || []).filter(r => r.appliedPromotionId === promo.id || r.discountId === promo.id).length;
            if (usedCount >= promo.customerLimit) {
                return { eligible: false, reason: `Bu kampanyayı en fazla ${promo.customerLimit} kez kullanabilirsiniz.` };
            }
        }

        // 2. Amount and Products
        const subtotal = cart.reduce((sum, item) => sum + (Number(item.price) * item.quantity), 0);

        // Min Amount Check
        if (promo.minAmount && subtotal < Number(promo.minAmount)) {
            return { eligible: false, reason: `Min. ₺${promo.minAmount} sepet tutarı gerekli. (Şu an: ₺${subtotal})` };
        }

        // Specific Product Scope Check
        if (promo.productScope === 'specific' || promo.productScope === 'specific_products') {
            const selectedList = promo.selectedProducts || [];
            const selectedCategories = promo.selectedCategories || []; // Support categories if added in future

            if (selectedList.length > 0 || selectedCategories.length > 0) {
                const hasRequiredItem = cart.some(item =>
                    selectedList.includes(item.prodId) ||
                    selectedList.includes(item.name) ||
                    selectedCategories.includes(item.categoryName)
                );
                if (!hasRequiredItem) {
                    return { eligible: false, reason: "Kampanyalı kategoriler/ürünlerden en az biri eklenmeli." };
                }
            }
        }

        // 3. Bundle/Buy-Get logic
        if (promo.campaignType === 'bundle' || promo.campaignType === 'coupon_bundle') {
            const buyItems = promo.buyItems || [];
            if (buyItems.length > 0) {
                const hasBuyItem = cart.some(item =>
                    buyItems.includes(item.prodId) ||
                    buyItems.includes(item.name)
                );
                if (!hasBuyItem) {
                    return { eligible: false, reason: "Kampanyalı ürünler sepetinizde bulunamadı." };
                }
            }
        }

        return { eligible: true };
    };

    // Auto-unapply if cart changes and becomes ineligible
    useEffect(() => {
        if (appliedPromotion) {
            const { eligible } = isPromotionEligible(appliedPromotion, cart, customer, redemptions);
            if (!eligible) {
                setAppliedPromotion(null);
            }
        }
    }, [cart, appliedPromotion, customer, redemptions]);

    // Helper to map snake_case DB row to camelCase for customer
    const mapCustomerRow = (row) => {
        if (!row) return null;
        return {
            id: row.id,
            firstName: row.first_name,
            lastName: row.last_name,
            name: row.name,
            email: row.email,
            phone: row.phone,
            tenantId: row.tenant_id,
            shortId: row.short_id,
            points: row.points,
            totalPoints: row.total_points,
            pointEarnings: row.point_earnings || [],
            visitTimestamps: row.visit_timestamps,
            interactionCount: row.interaction_count,
            purchaseCount: row.purchase_count,
            totalSpent: row.total_spent,
            loyaltyLevel: row.loyalty_level,
            visitRole: row.visit_role,
            notificationsEnabled: row.notifications_enabled,
            fcmTokensList: row.fcm_tokens_list,
            activeRewards: row.active_rewards || [],
            birthday: row.birthday,
            savedCoupons: row.saved_coupons || [],
            lastVisit: row.last_visit,
            createdAt: row.created_at,
            updatedAt: row.updated_at,
        };
    };

    // Helper to map snake_case DB row to camelCase for discount
    const mapDiscountRow = (row) => {
        if (!row) return null;
        return {
            id: row.id,
            tenantId: row.tenant_id,
            title: row.title,
            description: row.description,
            type: row.type,
            value: row.value,
            isActive: row.is_active,
            isReward: row.is_reward,
            requiredPoints: row.required_points,
            minAmount: row.min_amount,
            totalLimit: row.total_limit,
            customerLimit: row.customer_limit,
            expiryDate: row.expiry_date,
            startDate: row.start_date,
            usageCount: row.usage_count,
            target: row.target,
            loyaltyStatus: row.loyalty_status,
            visitRole: row.visit_role,
            selectedCustomers: row.selected_customers,
            productScope: row.product_scope,
            selectedProducts: row.selected_products,
            selectedCategories: row.selected_categories,
            campaignType: row.campaign_type,
            buyItems: row.buy_items,
            getItems: row.get_items,
            buyQuantity: row.buy_quantity,
            getQuantity: row.get_quantity,
            bundleDiscountValue: row.bundle_discount_value,
            applicableDays: row.applicable_days,
            birthdayOnly: row.birthday_only || false,
            code: (row.campaign_type === 'coupon' || row.campaign_type === 'coupon_bundle') ? (row.description || null) : null,
            createdAt: row.created_at,
        };
    };

    // Helper to map interaction row
    const mapInteractionRow = (row) => {
        if (!row) return null;
        return {
            id: row.id,
            customerId: row.customer_id,
            tenantId: row.tenant_id,
            type: row.type,
            content: row.content,
            points: row.points,
            pointsDeducted: row.points_deducted,
            discountId: row.discount_id,
            code: row.code,
            status: row.status,
            timestamp: row.timestamp,
            isAuto: row.is_auto,
            orderId: row.order_id,
            appliedPromotionId: row.applied_promotion_id,
            appliedPromotionTitle: row.applied_promotion_title,
            usedAt: row.used_at,
            items: row.items,
            totalAmount: row.total_amount,
            earnedPoints: row.earned_points,
            createdAt: row.created_at,
        };
    };

    // Helper to map order row to history format
    const mapOrderToHistory = (row) => {
        if (!row) return null;

        // For split payments, show only this customer's paid amount
        const payments = row.payments || [];
        const customerPayments = payments.filter(p => p.customerId === row.customer_id);
        const isSplitPayment = payments.length > 1 || (payments.length === 1 && customerPayments.length === 1 && Number(row.total_amount) !== customerPayments[0]?.amount);
        const displayAmount = isSplitPayment && customerPayments.length > 0
            ? customerPayments.reduce((sum, p) => sum + (p.amount || 0), 0)
            : row.total_amount;

        // Calculate earned points from customer's payment amount
        const displayPoints = isSplitPayment && customerPayments.length > 0
            ? customerPayments.reduce((sum, p) => sum + Math.floor((p.amount || 0) * 0.1), 0)
            : row.earned_points;

        // For item-based split payments, show the customer's specific items
        const customerItemsFromPayments = isSplitPayment
            ? customerPayments.flatMap(p => p.items || [])
            : [];
        const displayItems = customerItemsFromPayments.length > 0 ? customerItemsFromPayments : (row.items || []);
        const displayContent = displayItems.map(i => `${i.name} (${i.quantity}x)`).join(', ');

        return {
            id: row.id,
            customerId: row.customer_id,
            tenantId: row.tenant_id,
            type: 'Purchase',
            content: displayContent,
            status: row.status,
            timestamp: row.timestamp || row.created_at,
            items: displayItems,
            totalAmount: displayAmount,
            earnedPoints: displayPoints,
            appliedPromotionId: row.applied_promotion_id,
            appliedPromotionTitle: row.applied_promotion_title,
            tableNumber: row.table_number,
            createdAt: row.created_at,
            isSplitPayment,
        };
    };

    const fetchData = async () => {
        if (!tenantId) return;
        try {
            // Fetch Tenant (By ID or Slug)
            let tenantData = null;
            let resolvedTenantId = null;

            // Check if tenantId looks like a UUID
            const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(tenantId);

            if (isUUID) {
                const { data: tRow } = await supabase
                    .from('tenants')
                    .select('*')
                    .eq('id', tenantId)
                    .maybeSingle();
                if (tRow) {
                    tenantData = tRow;
                    resolvedTenantId = tRow.id;
                }
            }

            if (!tenantData) {
                // Try looking up by slug
                const { data: slugRows } = await supabase
                    .from('tenants')
                    .select('*')
                    .eq('slug', tenantId)
                    .limit(1);
                if (slugRows && slugRows.length > 0) {
                    tenantData = slugRows[0];
                    resolvedTenantId = tenantData.id;
                }
            }

            if (tenantData) {
                // Map snake_case tenant fields to camelCase for component compatibility
                const mappedTenant = {
                    ...tenantData,
                    loyaltyRules: tenantData.loyalty_rules,
                    loyaltyLevels: tenantData.loyalty_levels,
                    visitRoles: tenantData.visit_roles,
                    apiKeys: tenantData.api_keys,
                    tables: tenantData.tables, // Just added this column
                    theme: tenantData.theme, // JSON column, should already be an object
                };
                setTenant(mappedTenant);
            } else {
                console.error("Tenant not found:", tenantId);
                setError('tenant_not_found');
                setLoading(false);
                return;
            }

            setActualTenantId(resolvedTenantId);
            const targetTenantId = resolvedTenantId;

            // Resolve customerId from DB using global identity
            let resolvedCustomerId = customerId;
            const globalId = localStorage.getItem('crm_global_customer_id');

            // DB-driven resolution: find the customer record for this tenant using global email
            if (!resolvedCustomerId && globalId) {
                const { data: globalCustomer } = await supabase.from('customers').select('email').eq('id', globalId).maybeSingle();
                if (globalCustomer?.email) {
                    const { data: localMatch } = await supabase
                        .from('customers')
                        .select('id')
                        .eq('tenant_id', targetTenantId)
                        .eq('email', globalCustomer.email)
                        .maybeSingle();

                    if (localMatch) {
                        resolvedCustomerId = localMatch.id;
                        if (customerId !== resolvedCustomerId) setCustomerId(resolvedCustomerId);
                    }
                }
            }

            // If still no customerId but we have a global ID, the user might have the global record in THIS tenant
            if (!resolvedCustomerId && globalId) {
                const { data: directMatch } = await supabase
                    .from('customers')
                    .select('id')
                    .eq('id', globalId)
                    .eq('tenant_id', targetTenantId)
                    .maybeSingle();
                if (directMatch) {
                    resolvedCustomerId = directMatch.id;
                    setCustomerId(resolvedCustomerId);
                }
            }

            if (!resolvedCustomerId) {
                setError('customer_not_found');
                setLoading(false);
                return;
            }

            // Fetch Customer
            let customerData = null;

            const { data: cRow } = await supabase
                .from('customers')
                .select('*')
                .eq('id', resolvedCustomerId)
                .single();

            if (cRow) {
                customerData = mapCustomerRow(cRow);
            } else {
                // Try looking up by shortId
                const { data: shortIdRows } = await supabase
                    .from('customers')
                    .select('*')
                    .eq('tenant_id', targetTenantId)
                    .eq('short_id', resolvedCustomerId)
                    .limit(1);
                if (shortIdRows && shortIdRows.length > 0) {
                    customerData = mapCustomerRow(shortIdRows[0]);
                    // Update our internal ID to the real DB ID for interactions
                    setCustomerId(customerData.id);
                }
            }

            if (customerData) {
                if (!customerData.firstName && !customerData.lastName && customerData.name) {
                    const parts = customerData.name.trim().split(' ');
                    customerData.firstName = parts[0] || '';
                    customerData.lastName = parts.slice(1).join(' ') || '';
                }
                // Self-healing: If existing customer doesn't have a shortId, assign one now
                if (!customerData.shortId) {
                    const newShortId = generateShortId();
                    try {
                        await supabase
                            .from('customers')
                            .update({ short_id: newShortId })
                            .eq('id', customerData.id);
                        customerData.shortId = newShortId;
                        console.log("Auto-assigned shortId to existing customer:", newShortId);
                    } catch (e) {
                        console.error("Error auto-assigning shortId:", e);
                    }
                }

                setCustomer(customerData);
            } else {
                console.error("Customer not found:", customerId);
                setError('customer_not_found');
                setLoading(false);
                return;
            }

            setActualTenantId(resolvedTenantId);

            const allRedemptions = [];
            setRedemptions(allRedemptions);

            // Fetch Discounts
            const { data: dRows } = await supabase
                .from('discounts')
                .select('*')
                .eq('tenant_id', targetTenantId);
            const mappedDiscounts = (dRows || []).map(mapDiscountRow);
            setDiscounts(mappedDiscounts);

            // Kaydedilmiş kuponları DB'den yükle
            const savedCodes = Array.isArray(customerData?.savedCoupons) ? customerData.savedCoupons : [];
            if (savedCodes.length > 0) {
                const savedCouponObjects = savedCodes
                    .map(code => mappedDiscounts.find(d =>
                        d.code && d.code.toUpperCase() === String(code).toUpperCase() &&
                        (d.campaignType === 'coupon' || d.campaignType === 'coupon_bundle') &&
                        d.isActive !== false
                    ))
                    .filter(Boolean);
                setAddedCouponsRaw(savedCouponObjects);
            }

            // Fetch Notifications
            const { data: nRows } = await supabase
                .from('notifications')
                .select('*')
                .eq('tenant_id', targetTenantId)
                .order('created_at', { ascending: false })
                .limit(10);
            setNotifications((nRows || []).map(n => ({
                id: n.id,
                tenantId: n.tenant_id,
                title: n.title,
                body: n.body,
                imageUrl: n.image_url,
                target: n.target,
                actionLabel: n.action_label,
                actionLink: n.action_link,
                createdAt: n.created_at,
            })));

            // Request Push Notification Permission
            if (customerId) {
                setTimeout(async () => {
                    const res = await requestNotificationPermission(customerId);
                    if (res?.success) {
                        try {
                            await supabase.from('customers').update({ notifications_enabled: true }).eq('id', customerId);
                        } catch (err) {
                            console.error("Failed to enable notifications in DB:", err);
                        }
                    }
                }, 3000); // UI yüklendikten 3 saniye sonra sor
            }

            // Fetch Menu
            const menuData = await checkInService.fetchMenu(targetTenantId, tenantData?.active_menu_id);
            if (menuData) {
                setMenu(menuData);
            }

        } catch (err) {
            console.error("Portal Error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!actualTenantId) return;

        // Initial fetch
        const fetchInventory = async () => {
            const { data } = await supabase
                .from('product_stocks')
                .select('*')
                .eq('tenant_id', actualTenantId)
                .single();
            if (data) {
                setInventory(data.stocks || {});
            }
        };
        fetchInventory();

        // Realtime subscription
        const channel = supabase
            .channel(`product_stocks_${actualTenantId}`)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'product_stocks',
                filter: `tenant_id=eq.${actualTenantId}`
            }, (payload) => {
                if (payload.new) {
                    setInventory(payload.new.stocks || {});
                }
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [actualTenantId]);

    // rewards from 'customers' table (decentralized)
    const fetchInteractions = useCallback(async () => {
        if (!customer?.activeRewards || !actualTenantId) return;
        
        // Use the reward data directly from customer object
        const activeOnly = (customer.activeRewards || []).filter(it => it.status === 'active');
        setActiveRewardCodes(activeOnly);
    }, [customer?.activeRewards, actualTenantId]);

    useEffect(() => {
        fetchData();
    }, [tenantId]); // Trigger when URL param changes

    useEffect(() => {
        if (customerId && actualTenantId) {
            fetchInteractions();
        }
    }, [customerId, actualTenantId, fetchInteractions]);

    useEffect(() => {
        // Handle deep linking via query params (e.g., ?tab=notifications)
        const params = new URLSearchParams(window.location.search);
        const tabParam = params.get('tab');
        const notifId = params.get('notifId');
        const tableParam = params.get('table');

        if (tabParam && ['menu', 'notifications', 'promotions', 'rewards', 'cart', 'profile'].includes(tabParam)) {
            setActiveTab(tabParam);
        }

        // Table scan logging deprecated
        if (tableParam && actualTenantId) {
            console.log("Table scan detected via URL:", tableParam);
        }

        // Track notification interaction
        if (notifId && customerId && actualTenantId) {
            const trackInteraction = async () => {
                try {
                    await supabase.from('notification_interactions').insert({
                        notification_id: notifId,
                        customer_id: customerId,
                        tenant_id: actualTenantId,
                        timestamp: new Date().toISOString(),
                        platform: 'web'
                    });
                    console.log("Notification interaction tracked:", notifId);
                } catch (err) {
                    console.error("Error tracking notif interaction:", err);
                }
            };
            trackInteraction();
        }
    }, [actualTenantId, customerId]);

    // Persist changes to localStorage
    useEffect(() => {
        if (customerId) {
            localStorage.setItem(`cart_${customerId}`, JSON.stringify(cart));
        }
    }, [cart, customerId]);

    useEffect(() => {
        if (customerId) {
            localStorage.setItem(`lastTab_${customerId}`, activeTab);
        }
    }, [activeTab, customerId]);

    useEffect(() => {
        if (customerId) {
            localStorage.setItem(`tableNum_${customerId}`, tableNumber);
        }
    }, [tableNumber, customerId]);

    useEffect(() => {
        if (customerId) {
            localStorage.setItem(`promo_${customerId}`, JSON.stringify(appliedPromotion));
        }
    }, [appliedPromotion, customerId]);

    // Persist active reward codes to localStorage
    useEffect(() => {
        if (tenantId) {
            localStorage.setItem(`reward_codes_${tenantId}`, JSON.stringify(activeRewardCodes));
        }
    }, [activeRewardCodes, tenantId]);



    // Real-time Order History Listener
    useEffect(() => {
        if (!actualTenantId || !customerId) return;

        // Initial fetch from 'orders' table - ONLY PAID ORDERS
        const fetchHistory = async () => {
            const { data } = await supabase
                .from('orders')
                .select('*')
                .eq('customer_id', customerId)
                .eq('tenant_id', actualTenantId)
                .eq('status', 'paid')
                .order('created_at', { ascending: false })
                .limit(50);
            if (data) {
                setHistory(data.map(mapOrderToHistory));
            }
        };
        fetchHistory();

        // Realtime subscription to 'orders'
        const channel = supabase
            .channel(`orders_${actualTenantId}_${customerId}`)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'orders',
                filter: `customer_id=eq.${customerId}`
            }, async () => {
                // Re-fetch on any change - ONLY PAID ORDERS
                const { data } = await supabase
                    .from('orders')
                    .select('*')
                    .eq('customer_id', customerId)
                    .eq('tenant_id', actualTenantId)
                    .eq('status', 'paid')
                    .order('created_at', { ascending: false })
                    .limit(50);
                if (data) {
                    setHistory(data.map(mapOrderToHistory));
                }
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [actualTenantId, customerId]);

    // Native Back Gesture Handler
    useEffect(() => {
        const handlePopState = (event) => {
            // Priority: Close modals first
            if (showCode) {
                setShowCode(null);
                return;
            }

            if (selectedNotification) {
                setSelectedNotification(null);
                return;
            }

            // Only switch to menu if we are in another sub-tab and the event state doesn't belong to a category
            if (!event.state?.view && activeTab !== 'menu') {
                setActiveTab('menu');
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [activeTab, selectedNotification, showCode]);

    const changeTab = (tabId) => {
        if (activeTab === tabId) return;

        // Simple state push for back button support
        window.history.pushState({ view: 'tab', id: tabId }, '');
        setActiveTab(tabId);
    };
    const handleApplyReward = (code) => {
        if (!code) {
            setAppliedReward(null);
            return;
        }

        const codeToFind = code.trim().toUpperCase();
        const interaction = activeRewardCodes.find(it => it.code === codeToFind && it.status === 'active');
        
        if (!interaction) {
            showAlert("Geçersiz veya kullanılmış bir kod girdiniz.", "error");
            return;
        }

        // Find the matching discount by title or id
        const discount = discounts.find(d => d.title === interaction.title || d.id === interaction.discountId);
        
        if (!discount) {
            showAlert("Bu koda ait kampanya verisi bulunamadı.", "error");
            return;
        }

        // Birthday-only check
        if (discount.birthdayOnly) {
            const today = new Date();
            if (!customer.birthday) {
                showAlert("Bu indirim sadece doğum gününüzde kullanılabilir.", "warning");
                return;
            }
            const [bY, bM, bD] = customer.birthday.split('-').map(Number);
            if (today.getDate() !== bD || today.getMonth() !== bM - 1) {
                showAlert("Bu indirim sadece doğum gününüzde kullanılabilir.", "warning");
                return;
            }
        }

        // Check product match if specific
        if (discount.productScope === 'specific' || discount.productScope === 'specific_products') {
            const selectedList = discount.selectedProducts || [];
            const hasMatch = cart.some(item =>
                selectedList.includes(item.prodId) ||
                selectedList.includes(item.name)
            );
            if (!hasMatch) {
                showAlert("Bu kupon sepetinizdeki ürünler için geçerli değil.", "warning");
                return;
            }
        }

        setAppliedReward({ interaction, discount });
        setAppliedPromotion(null); // Mutual exclusivity: remove promotion if reward applied
        showAlert("Kupon başarıyla uygulandı!", "success");
        setRewardCode('');
    };

    const openNotification = (notif) => {
        window.history.pushState({ view: 'modal', type: 'notif' }, '');
        setSelectedNotification(notif);
        markAsRead(notif.id);
    };

    const closeCodeModal = () => {
        window.history.back();
    };

    const closeNotificationModal = () => {
        window.history.back();
    };

    const handleRedeem = async (discount) => {
        // Safety check for start date
        if (discount.startDate && new Date(discount.startDate) > new Date()) {
            showAlert(t('discounts.comingSoon'), "warning");
            return;
        }

        // Safety check for points if it's a reward
        if (discount.isReward && (customer.points || 0) < (discount.requiredPoints || 0)) {
            showAlert(t('discounts.insufficientPoints'), "warning");
            return;
        }

        // For rewards: block if customer already has an active (unused) instance of this reward
        if (discount.isReward) {
            const activeOfSame = (customer.activeRewards || []).filter(
                r => r.discountId === discount.id && r.status === 'active'
            );
            if (activeOfSame.length > 0) {
                showAlert('Bu ödülü zaten aldınız. Kullandıktan sonra tekrar alabilirsiniz.', 'warning');
                return;
            }
        }

        setRedeeming(discount.id);
        try {
            // 1. Generate unique 7-digit code
            const code = Math.random().toString(36).substring(2, 9).toUpperCase();

            // 2. Perform Claim: Deduct points and store reward in customer record (Atomic)
            if (discount.isReward) {
                const { data: currentCust } = await supabase
                    .from('customers')
                    .select('points, active_rewards')
                    .eq('id', customerId)
                    .single();
                
                const newPoints = (currentCust?.points || 0) - Number(discount.requiredPoints || 0);
                const rewardPayload = {
                    id: crypto.randomUUID(), // Local unique ID for the claim
                    code: code,
                    status: 'active',
                    title: discount.title,
                    discountId: discount.id,
                    timestamp: new Date().toISOString(),
                    content: discount.isReward
                        ? `${t('discounts.claimedReward')}: ${discount.title}`
                        : `${t('discounts.usedDiscount')}: ${discount.title}`
                };

                const updatedRewards = [
                    ...(currentCust?.active_rewards || []),
                    rewardPayload
                ];

                const { error: custErr } = await supabase
                    .from('customers')
                    .update({ 
                        points: newPoints,
                        active_rewards: updatedRewards
                    })
                    .eq('id', customerId);
                
                if (custErr) {
                    console.error("[Portal] Customer Points Update Error:", custErr);
                    throw custErr;
                }
            }

            // 3. Update discount usage count
            const { data: currentDiscount } = await supabase
                .from('discounts')
                .select('usage_count')
                .eq('id', discount.id)
                .single();
            
            const { error: discErr } = await supabase
                .from('discounts')
                .update({ usage_count: (currentDiscount?.usage_count || 0) + 1 })
                .eq('id', discount.id);
            
            if (discErr) console.error("[Portal] Discount Usage Update Error:", discErr);

            // 4. Activity Log (optional, no DB table interaction for logs needed)
            console.log(`[Redeem] Reward ${discount.title} claimed by ${customerId}`);

            // 5. Show code modal
            setShowCode(code);
            window.history.pushState({ view: 'modal', type: 'code' }, '');

            // Refresh counts and interactions
            fetchData();
            fetchInteractions();
        } catch (err) {
            console.error(err);
            showAlert("İşlem sırasında bir hata oluştu.", "error");
        } finally {
            setRedeeming(null);
        }
    };

    const addToCart = (product) => {
        if (!orderingEnabled) return;
        const prodId = `${product.categoryName}_${product.name}`;
        setCart(prev => {
            const existing = prev.find(i => i.prodId === prodId);
            if (existing) {
                return prev.map(i => i.prodId === prodId ? { ...i, quantity: i.quantity + 1 } : i);
            }
            return [...prev, { ...product, prodId, quantity: 1 }];
        });
    };

    const removeFromCart = (prodId) => {
        setCart(prev => prev.filter(i => i.prodId !== prodId));
    };

    const updateCartQuantity = (prodId, delta) => {
        setCart(prev => prev.map(i => {
            if (i.prodId === prodId) {
                const newQty = Math.max(1, i.quantity + delta);
                return { ...i, quantity: newQty };
            }
            return i;
        }));
    };

    const calculateCartTotal = () => {
        const subtotal = cart.reduce((sum, item) => sum + (Number(item.price) * item.quantity), 0);
        let promoDiscount = 0;
        let promoDetails = [];

        if (appliedPromotion) {
            if (appliedPromotion.campaignType === 'bundle' || appliedPromotion.campaignType === 'coupon_bundle') {
                const buyItems = appliedPromotion.buyItems || [];
                const getItems = appliedPromotion.getItems?.length > 0 ? appliedPromotion.getItems : buyItems;
                const buyQty = Number(appliedPromotion.buyQuantity) || 1;
                const getQty = Number(appliedPromotion.getQuantity) || 1;
                const discountPercent = Number(appliedPromotion.bundleDiscountValue) || 100;

                // Find all matching buy items and their total count
                const totalBuyInCart = cart
                    .filter(item => buyItems.includes(item.prodId) || buyItems.includes(item.name))
                    .reduce((sum, item) => sum + item.quantity, 0);

                const completedSets = Math.min(1, Math.floor(totalBuyInCart / buyQty));

                if (completedSets > 0) {
                    const maxDiscountable = completedSets * getQty;
                    // Identify target "get" items, sorting by cheapest for standard behavior
                    const targetItems = cart
                        .filter(item => getItems.includes(item.prodId) || getItems.includes(item.name))
                        .sort((a, b) => Number(a.price) - Number(b.price));

                    let remainingDiscountable = maxDiscountable;
                    for (const itm of targetItems) {
                        if (remainingDiscountable <= 0) break;
                        const canDiscount = Math.min(itm.quantity, remainingDiscountable);
                        const amount = canDiscount * Number(itm.price) * (discountPercent / 100);
                        promoDiscount += amount;
                        promoDetails.push({ name: itm.name, amount, quantity: canDiscount });
                        remainingDiscountable -= canDiscount;
                    }
                }
            } else if (appliedPromotion.type === 'multiplier') {
                promoDiscount = 0;
            } else {
                if (appliedPromotion.type === 'percentage') {
                    const isSpecific = appliedPromotion.productScope === 'specific' || appliedPromotion.productScope === 'specific_products';
                    if (isSpecific) {
                        const selectedList = appliedPromotion.selectedProducts || [];
                        const selectedCategories = appliedPromotion.selectedCategories || [];

                        const matchingItems = cart.filter(item =>
                            selectedList.includes(item.prodId) ||
                            selectedList.includes(item.name) ||
                            selectedCategories.includes(item.categoryName)
                        ).sort((a, b) => Number(a.price) - Number(b.price)); // Sort by price ascending (cheapest first)

                        if (matchingItems.length > 0) {
                            // Apply discount only to the first matching item found (the cheapest one)
                            const amount = Number(matchingItems[0].price) * (Number(appliedPromotion.value) / 100);
                            promoDiscount = amount;
                            promoDetails.push({ name: matchingItems[0].name, amount, quantity: 1 });
                        }
                    } else {
                        promoDiscount = subtotal * (Number(appliedPromotion.value) / 100);
                    }
                } else {
                    promoDiscount = Number(appliedPromotion.value);
                }
            }
        }

        let totalAfterPromo = Math.max(0, subtotal - promoDiscount);
        let rewardDiscount = 0;
        let rewardDetails = [];

        // Apply Reward Code Discount (Usually 1 free product)
        if (appliedReward) {
            const { discount } = appliedReward;
            const isSpecific = discount.productScope === 'specific' || discount.productScope === 'specific_products';

            if (isSpecific) {
                const selectedList = discount.selectedProducts || [];
                const selectedCategories = discount.selectedCategories || [];
                const matchingItems = cart.filter(item =>
                    selectedList.includes(item.prodId) ||
                    selectedList.includes(item.name) ||
                    selectedCategories.includes(item.categoryName)
                ).sort((a, b) => Number(a.price) - Number(b.price)); // Sort by price ascending (cheapest first)

                if (matchingItems.length > 0) {
                    const amount = Number(matchingItems[0].price);
                    rewardDiscount = amount;
                    rewardDetails.push({ name: matchingItems[0].name, amount, quantity: 1 });
                }
            } else if (discount.type === 'percentage') {
                rewardDiscount = totalAfterPromo * (Number(discount.value) / 100);
            } else if (discount.type === 'fixed') {
                rewardDiscount = Number(discount.value);
            }
        }

        const finalTotal = Math.max(0, totalAfterPromo - rewardDiscount);

        return {
            total: finalTotal,
            subtotal,
            promoDiscount,
            promoDetails,
            rewardDiscount,
            rewardDetails
        };
    };

    const calculateEarnedPoints = (total) => {
        const isPointsEnabled = tenant?.loyaltyRules?.purchasePointsEnabled !== false;
        if (!isPointsEnabled) return 0;

        const baseMultiplier = Number(tenant?.loyaltyRules?.purchasePointsMultiplier ?? 0.1);

        if (appliedPromotion && appliedPromotion.type === 'multiplier') {
            const promoMultiplierValue = Number(appliedPromotion.value) || 1;
            const isSpecific = appliedPromotion.productScope === 'specific' || appliedPromotion.productScope === 'specific_products';

            if (isSpecific) {
                const selectedList = appliedPromotion.selectedProducts || [];
                const selectedCategories = appliedPromotion.selectedCategories || [];

                let eligibleSubtotal = 0;
                let nonEligibleSubtotal = 0;

                cart.forEach(item => {
                    const isEligible = selectedList.includes(item.prodId) ||
                        selectedList.includes(item.name) ||
                        selectedCategories.includes(item.categoryName);

                    const itemTotal = Number(item.price) * item.quantity;
                    if (isEligible) {
                        eligibleSubtotal += itemTotal;
                    } else {
                        nonEligibleSubtotal += itemTotal;
                    }
                });

                // Calculate points separately for eligible and non-eligible items
                const pointsFromEligible = eligibleSubtotal * baseMultiplier * promoMultiplierValue;
                const pointsFromNonEligible = nonEligibleSubtotal * baseMultiplier;
                return Math.floor(pointsFromEligible + pointsFromNonEligible);
            } else {
                // Multiplier applies to the whole cart
                return Math.floor(total * baseMultiplier * promoMultiplierValue);
            }
        }

        // Default: just apply base multiplier to the total paid
        return Math.floor(total * baseMultiplier);
    };

    const handlePayment = async () => {
        if (!orderingEnabled) return;
        if (cart.length === 0) return;
        if (!tableNumber) {
            showAlert(t('portal.selectTableNumber', 'Lütfen bir masa numarası seçin.'), 'warning');
            return;
        }
        setPaymentLoading(true);
        try {
            const { total, subtotal, promoDiscount, rewardDiscount } = calculateCartTotal();
            const discountAmount = subtotal - total;

            // Calculate Points if enabled
            const earnedPoints = calculateEarnedPoints(total);

            // Resolve table display name for the order record
            const resolvedTableObj = (tenant?.tables || []).find(t => String(t.id) === String(tableNumber));
            const resolvedTableDisplay = resolvedTableObj ? resolvedTableObj.name : tableNumber;
            const finalTableNumber = resolvedTableDisplay.startsWith('Masa') ? resolvedTableDisplay.replace('Masa ', '') : resolvedTableDisplay;

            const orderData = {
                customer_id: customerId,
                tenant_id: actualTenantId || tenantId,
                customer_name: `${customer.firstName || ''} ${customer.lastName || ''}`.trim(),
                items: cart,
                total_amount: total,
                remaining_amount: total,
                subtotal,
                discount_amount: discountAmount,
                earned_points: earnedPoints,
                applied_promotion_id: appliedPromotion?.id || null,
                applied_promotion_title: appliedPromotion?.title || null,
                table_number: finalTableNumber,
                applied_reward_id: null, // Local string ID is not a UUID, so keep null
                applied_reward_code: appliedReward?.interaction?.code || null,
                status: 'pending',
                timestamp: new Date().toISOString(),
                created_at: new Date().toISOString()
            };

            // 1. Create order in 'orders' collection
            const { data: orderRow, error: orderErr } = await supabase
                .from('orders')
                .insert(orderData)
                .select()
                .single();
            if (orderErr) throw orderErr;

            // 2. Update last visit (points and total spend will be added when approved)
            await supabase
                .from('customers')
                .update({ last_visit: new Date().toISOString() })
                .eq('id', customerId);

            // If promotion was used, update usage count
            if (appliedPromotion) {
                const { data: promoRow } = await supabase
                    .from('discounts')
                    .select('usage_count')
                    .eq('id', appliedPromotion.id)
                    .single();
                await supabase
                    .from('discounts')
                    .update({ usage_count: (promoRow?.usage_count || 0) + 1 })
                    .eq('id', appliedPromotion.id);
            }

            // 4. Mark reward as used in customer.active_rewards
            if (appliedReward) {
                const { data: currentCust } = await supabase
                    .from('customers')
                    .select('active_rewards')
                    .eq('id', customerId)
                    .single();

                if (currentCust) {
                    const updatedRewards = (currentCust.active_rewards || []).map(r =>
                        r.id === appliedReward.interaction.id || r.code === appliedReward.interaction.code
                            ? { ...r, status: 'used', usedAt: new Date().toISOString() }
                            : r
                    );
                    await supabase
                        .from('customers')
                        .update({ active_rewards: updatedRewards })
                        .eq('id', customerId);
                }

                // Update local state
                setActiveRewardCodes(prev => prev.filter(r => r.code !== appliedReward.interaction.code));
            }

            // Stokları güncelle (Siparişi aksatmaması için try-catch içinde)
            try {
                const stockTenantId = actualTenantId || tenantId;
                const { data: invData } = await supabase
                    .from('product_stocks')
                    .select('*')
                    .eq('tenant_id', stockTenantId)
                    .single();
                if (invData) {
                    const currentStocks = invData.stocks || {};
                    const newStocks = { ...currentStocks };

                    cart.forEach(item => {
                        if (newStocks[item.prodId] !== undefined) {
                            newStocks[item.prodId] = Math.max(0, newStocks[item.prodId] - item.quantity);
                        }
                    });

                    await supabase
                        .from('product_stocks')
                        .update({
                            stocks: newStocks,
                            updated_at: new Date().toISOString()
                        })
                        .eq('tenant_id', stockTenantId);
                }
            } catch (invErr) {
                console.error("Inventory update error:", invErr);
            }

            setOrderSuccess(true);
            setCart([]);
            setAppliedPromotion(null);
            setAppliedReward(null);
            setRewardCode('');
            // UI feedback
            showAlert(t('portal.orderSuccess', 'Siparişiniz başarıyla alındı!') + (earnedPoints > 0 ? ` Ödemeniz onaylandığında +${earnedPoints} Puan bakiyenize eklenecektir.` : ''), 'success', 5000);
            setActiveTab('menu');
            // Re-fetch customer data to show new points
            fetchData();
        } catch (err) {
            console.error("Payment error:", err);
            showAlert("Ödeme işlemi sırasında bir hata oluştu.", "error");
        } finally {
            setPaymentLoading(false);
        }
    };

    // Handle online payment via iyzico (tenant's own credentials)
    const handleOnlinePayment = async () => {
        if (!orderingEnabled) return;
        if (cart.length === 0) return;
        if (!tableNumber) {
            showAlert(t('portal.selectTableNumber', 'Lütfen bir masa numarası seçin.'), 'warning');
            return;
        }
        setPaymentLoading(true);
        try {
            const { total, subtotal, promoDiscount, rewardDiscount } = calculateCartTotal();
            const discountAmount = subtotal - total;
            const earnedPoints = calculateEarnedPoints(total);

            const resolvedTableObj = (tenant?.tables || []).find(t => String(t.id) === String(tableNumber));
            const resolvedTableDisplay = resolvedTableObj ? resolvedTableObj.name : tableNumber;
            const finalTableNumber = resolvedTableDisplay.startsWith('Masa') ? resolvedTableDisplay.replace('Masa ', '') : resolvedTableDisplay;

            // 1. Create the order first (status: pending, payment_status: awaiting)
            const orderData = {
                customer_id: customerId,
                tenant_id: actualTenantId || tenantId,
                customer_name: `${customer.firstName || ''} ${customer.lastName || ''}`.trim(),
                items: cart,
                total_amount: total,
                remaining_amount: total,
                subtotal,
                discount_amount: discountAmount,
                earned_points: earnedPoints,
                applied_promotion_id: appliedPromotion?.id || null,
                applied_promotion_title: appliedPromotion?.title || null,
                table_number: finalTableNumber,
                applied_reward_code: appliedReward?.interaction?.code || null,
                status: 'pending',
                payment_status: 'awaiting',
                timestamp: new Date().toISOString(),
                created_at: new Date().toISOString()
            };

            const { data: orderRow, error: orderErr } = await supabase
                .from('orders')
                .insert(orderData)
                .select()
                .single();
            if (orderErr) throw orderErr;

            // 2. Detect payment provider and call the correct checkout API
            // TODO: Portal payment endpoints temporarily removed (Vercel Hobby function limit)
            let checkoutApi = null;
            let provider = null;

            if (tenant?.integrations?.param?.enabled) {
                checkoutApi = '/api/create-checkout';
                provider = 'param';
            }

            if (!checkoutApi) {
                throw new Error('Ödeme sistemi henüz yapılandırılmamış.');
            }

            const resp = await fetch(checkoutApi, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tenantId: actualTenantId || tenantId,
                    orderId: orderRow.id,
                    customerId,
                    customerName: `${customer.firstName || ''} ${customer.lastName || ''}`.trim(),
                    customerEmail: customer.email || '',
                    customerPhone: customer.phone || '',
                    items: cart,
                    totalAmount: total
                })
            });

            const data = await resp.json();
            if (!resp.ok) throw new Error(data.error || 'Checkout failed');

            // 3. Show checkout form in modal or redirect
            if (provider === 'paytr') {
                setCheckoutModal({ iframeUrl: data.iframeUrl, token: data.token, provider: 'paytr' });
            } else if (provider === 'iyzico') {
                setCheckoutModal({ html: data.checkoutFormContent, token: data.token, provider: 'iyzico' });
            } else if (provider === 'param') {
                // Param usually works best with full redirect
                window.location.href = data.checkoutUrl;
                return;
            }

            // Update promotion usage + reward marking (same as handlePayment)
            if (appliedPromotion) {
                const { data: promoRow } = await supabase
                    .from('discounts')
                    .select('usage_count')
                    .eq('id', appliedPromotion.id)
                    .single();
                await supabase
                    .from('discounts')
                    .update({ usage_count: (promoRow?.usage_count || 0) + 1 })
                    .eq('id', appliedPromotion.id);
            }

            if (appliedReward) {
                const { data: currentCust } = await supabase
                    .from('customers')
                    .select('active_rewards')
                    .eq('id', customerId)
                    .single();

                if (currentCust) {
                    const updatedRewards = (currentCust.active_rewards || []).map(r =>
                        r.id === appliedReward.interaction.id || r.code === appliedReward.interaction.code
                            ? { ...r, status: 'used', usedAt: new Date().toISOString() }
                            : r
                    );
                    await supabase
                        .from('customers')
                        .update({ active_rewards: updatedRewards })
                        .eq('id', customerId);
                }
                setActiveRewardCodes(prev => prev.filter(r => r.code !== appliedReward.interaction.code));
            }

            // Stok guncelle
            try {
                const stockTenantId = actualTenantId || tenantId;
                const { data: invData } = await supabase
                    .from('product_stocks')
                    .select('*')
                    .eq('tenant_id', stockTenantId)
                    .single();
                if (invData) {
                    const currentStocks = invData.stocks || {};
                    const newStocks = { ...currentStocks };
                    cart.forEach(item => {
                        if (newStocks[item.prodId] !== undefined) {
                            newStocks[item.prodId] = Math.max(0, newStocks[item.prodId] - item.quantity);
                        }
                    });
                    await supabase
                        .from('product_stocks')
                        .update({ stocks: newStocks, updated_at: new Date().toISOString() })
                        .eq('tenant_id', stockTenantId);
                }
            } catch (invErr) {
                console.error("Inventory update error:", invErr);
            }

            // Clear cart
            setCart([]);
            setAppliedPromotion(null);
            setAppliedReward(null);
            setRewardCode('');

        } catch (err) {
            console.error("Online payment error:", err);
            showAlert("Ödeme başlatılamadı: " + (err.message || 'Bilinmeyen hata'), "error");
        } finally {
            setPaymentLoading(false);
        }
    };

    // Handle payment result from URL (after iyzico callback redirect)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const paymentResult = params.get('payment');
        const orderId = params.get('orderId');

        if (paymentResult) {
            // Close any open checkout modal
            setCheckoutModal(null);

            if (paymentResult === 'success') {
                setOrderSuccess(true);
                showAlert('Ödemeniz başarıyla alındı! Siparişiniz hazırlanıyor.', 'success');
                setActiveTab('menu');
                fetchData();
            } else if (paymentResult === 'failed') {
                showAlert('Ödeme başarısız oldu. Lütfen tekrar deneyin veya masada ödeyin.', 'error');
            } else if (paymentResult === 'error') {
                showAlert('Ödeme işleminde bir hata oluştu.', 'error');
            }

            // Clean URL params
            const url = new URL(window.location);
            url.searchParams.delete('payment');
            url.searchParams.delete('orderId');
            url.searchParams.delete('reason');
            window.history.replaceState({}, '', url);
        }
    }, []);

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setPaymentLoading(true);
        try {
            const updateData = {
                first_name: customer.firstName || '',
                last_name: customer.lastName || '',
                email: customer.email || '',
                phone: customer.phone || '',
                birthday: customer.birthday || '',
                updated_at: new Date().toISOString()
            };

            // Mevcut tenant kaydını güncelle
            await supabase
                .from('customers')
                .update(updateData)
                .eq('id', customerId);

            // Aynı email ile diğer tenant kayıtlarını da güncelle (doğum günü, ad, telefon senkronizasyonu)
            if (customer.email) {
                await supabase
                    .from('customers')
                    .update({
                        first_name: updateData.first_name,
                        last_name: updateData.last_name,
                        phone: updateData.phone,
                        birthday: updateData.birthday,
                        updated_at: updateData.updated_at
                    })
                    .eq('email', customer.email)
                    .neq('id', customerId);
            }

            showAlert(t('common.success', 'Bilgileriniz başarıyla güncellendi!'), 'success');
            setActiveTab('profile');
        } catch (err) {
            console.error("Profile Update error:", err);
            showAlert(t('common.error', 'Bilgiler güncellenirken bir hata oluştu.'), 'error');
        } finally {
            setPaymentLoading(false);
        }
    };


    if (loading) return (
        <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-main)' }}>
            <div className="spinner"></div>
        </div>
    );

    if (error || !customer) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-main)', padding: '2rem' }}>
                <div className="card glass-premium" style={{ maxWidth: '450px', width: '100%', padding: '3rem', textAlign: 'center' }}>
                    <div style={{ width: '80px', height: '80px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 2rem' }}>
                        <XCircle size={40} color="#ef4444" />
                    </div>
                    {error === 'tenant_not_found' ? (
                        <>
                            <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '1rem' }}>İşletme Bulunamadı</h1>
                            <p style={{ color: 'var(--text-muted)', marginBottom: '1rem', lineHeight: 1.6 }}>
                                Girdiğiniz adrese ait bir işletme kaydı bulunamadı. Lütfen bağlantıyı kontrol edin.
                            </p>
                            <p style={{ color: 'red', padding: '1rem', background: '#ffebee', borderRadius: '8px', fontSize: '0.85rem' }}>
                                Aradığımız kimlik: {tenantId} <br />
                                (Lütfen bu kutuyu bana kopyalayın)
                            </p>
                        </>
                    ) : (
                        <>
                            <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '1rem' }}>Müşteri Bulunamadı</h1>
                            <p style={{ color: 'var(--text-muted)', marginBottom: '2rem', lineHeight: 1.6 }}>
                                Bu işletmeye ait kayıtlı bir profiliniz bulunamadı. QR kodunu tekrar okutarak kayıt olabilirsiniz.
                            </p>
                        </>
                    )}
                    <button
                        onClick={() => navigate('/')}
                        className="btn-premium"
                        style={{ width: '100%', background: 'var(--primary)', color: 'white', padding: '12px', borderRadius: '12px', fontWeight: 700 }}
                    >
                        Ana Sayfaya Dön
                    </button>
                    <div style={{ marginTop: '1.5rem', fontSize: '0.75rem', opacity: 0.5 }}>
                        ID: {customerId} / {tenantId}
                    </div>
                </div>
            </div>
        );
    }

    const primaryColor = '#6f4e37';

    const allValidPromotions = (discounts || []).filter(d => {
        if (d.isActive === false) return false;
        if (d.isReward) return false;

        // Birthday-only check: only show on customer's birthday, max 1 per year
        if (d.birthdayOnly) {
            if (!customer.birthday) return false;
            const today = new Date();
            const [bY, bM, bD] = customer.birthday.split('-').map(Number);
            if (today.getDate() !== bD || today.getMonth() !== bM - 1) return false;
            // Check if already used this year
            const thisYear = today.getFullYear();
            const usedThisYear = history.some(h =>
                h.appliedPromotionId === d.id &&
                new Date(h.timestamp).getFullYear() === thisYear
            );
            if (usedThisYear) return false;
        }

        // Target filtering
        let match = false;

        if (d.target === 'all') match = true;
        else if (d.target === 'groups') {
            const allowedLevels = Array.isArray(d.loyaltyStatus) ? d.loyaltyStatus : (d.loyaltyStatus ? [d.loyaltyStatus] : []);
            const allowedRoles = Array.isArray(d.visitRole) ? d.visitRole : (d.visitRole ? [d.visitRole] : []);
            if (allowedLevels.includes(customer.loyaltyLevel) || allowedRoles.includes(customer.visitRole)) match = true;
        }
        else if (d.target === 'visit_role') {
            const allowed = Array.isArray(d.visitRole) ? d.visitRole : (d.visitRole ? [d.visitRole] : []);
            if (allowed.includes(customer.visitRole)) match = true;
        }
        else if (d.target === 'customers' && d.selectedCustomers?.includes(customerId)) match = true;
        if (!match) return false;

        // Expiry check
        if (d.expiryDate) {
            const expiry = new Date(d.expiryDate);
            if (expiry < new Date()) return false;
        }
        // Total Limit check
        if (d.totalLimit && (d.usageCount || 0) >= d.totalLimit) return false;

        // Customer limit check
        if (d.customerLimit) {
            const usedCount = (redemptions || []).filter(r => r.discountId === d.id).length;
            if (usedCount >= d.customerLimit) return false;
        }

        return true;
    });

    // Kampanyalar listesi: kuponları çıkar (elle eklenir), doğum günü en alta
    const campaigns = allValidPromotions
        .filter(d => d.campaignType !== 'coupon' && d.campaignType !== 'coupon_bundle')
        .sort((a, b) => {
            if (a.birthdayOnly && !b.birthdayOnly) return 1;
            if (!a.birthdayOnly && b.birthdayOnly) return -1;
            return 0;
        });

    const rewards = (discounts || []).filter(d => {
        if (d.isActive === false) return false;
        if (!d.isReward) return false;

        // Target filtering
        let match = false;
        if (d.target === 'all') match = true;
        else if (d.target === 'groups') {
            const allowedLevels = Array.isArray(d.loyaltyStatus) ? d.loyaltyStatus : (d.loyaltyStatus ? [d.loyaltyStatus] : []);
            const allowedRoles = Array.isArray(d.visitRole) ? d.visitRole : (d.visitRole ? [d.visitRole] : []);
            if (allowedLevels.includes(customer.loyaltyLevel) || allowedRoles.includes(customer.visitRole)) match = true;
        }
        else if (d.target === 'visit_role') {
            const allowed = Array.isArray(d.visitRole) ? d.visitRole : (d.visitRole ? [d.visitRole] : []);
            if (allowed.includes(customer.visitRole)) match = true;
        }
        else if (d.target === 'customers' && d.selectedCustomers?.includes(customerId)) match = true;

        return match;
    });

    const filteredNotifications = (notifications || []).filter(n => {
        if (n.target === 'all') return true;
        // if (n.target === statusLabel) return true;
        return false;
    });

    const unreadCount = (filteredNotifications || []).filter(n => !readNotifications.includes(n.id)).length;

    const markAsRead = (id) => {
        if (!readNotifications.includes(id)) {
            const updated = [...readNotifications, id];
            setReadNotifications(updated);
            localStorage.setItem(`read_notifs_${customerId}`, JSON.stringify(updated));
        }
    };

    const usedDiscounts = redemptions;

    return (
        <div style={{ minHeight: '100vh', background: 'var(--bg-main)', color: 'var(--text-main)', paddingBottom: '6rem' }} className="fade-in">
            {/* <PWAInstallPrompt /> */}
            {/* Header */}
            <div style={{
                background: tenant?.banner_url ? `url(${tenant.banner_url}) center/cover no-repeat` : primaryColor,
                color: 'var(--text-on-primary)',
                padding: '2rem 1.25rem',
                position: 'relative',
                minHeight: '220px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: '1.5rem'
            }}>
                {/* Banner Overlay for readability if banner exists */}
                {tenant?.banner_url && (
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.5))', zIndex: 0 }}></div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr 48px', alignItems: 'center', gap: '10px' }}>
                    <button
                        onClick={() => {
                            if (activeTab !== 'menu') {
                                setActiveTab('menu');
                            } else {
                                navigate('/portal');
                            }
                        }}
                        style={{
                            background: 'var(--bg-card)',
                            border: 'none',
                            color: 'var(--text-main)',
                            width: '42px',
                            height: '42px',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            transition: 'all 0.2s',
                            position: 'relative',
                            zIndex: 10
                        }}
                    >
                        <ArrowLeft size={20} strokeWidth={2.5} />
                    </button>

                    <div style={{
                        justifySelf: 'center',
                        background: 'var(--bg-card)',
                        padding: '10px 24px',
                        borderRadius: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                        minHeight: '52px',
                        position: 'relative',
                        zIndex: 10
                    }}>
                        {tenant?.theme?.logoUrl && (
                            <img src={tenant.theme.logoUrl} alt="Logo" style={{ height: '36px', maxWidth: '120px', objectFit: 'contain', borderRadius: '10px' }} />
                        )}
                        <span style={{ fontWeight: 900, color: 'var(--text-main)', fontSize: '1rem', letterSpacing: '0.8px' }}>{tenant?.name}</span>
                    </div>

                    <div style={{ width: '42px', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            onClick={() => setActiveTab('notifications')}
                            style={{
                                background: 'white',
                                border: 'none',
                                color: 'var(--text-main)',
                                width: '42px',
                                height: '42px',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                position: 'relative',
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                transition: 'all 0.2s',
                                zIndex: 10
                            }}
                        >
                            <Bell size={20} strokeWidth={2.5} />
                            {unreadCount > 0 && (
                                <span style={{
                                    position: 'absolute',
                                    top: '4px',
                                    right: '4px',
                                    width: '10px',
                                    height: '10px',
                                    background: '#ef4444',
                                    borderRadius: '50%',
                                    border: '2px solid white',
                                    boxShadow: '0 0 5px rgba(239, 68, 68, 0.4)'
                                }}></span>
                            )}
                        </button>
                    </div>
                </div>

                {/* User Info Card */}
                {(() => {
                    const currentTier = calculateLoyaltyLevel(customer, tenant);
                    const tierColor = currentTier?.color || primaryColor;
                    const visitRole = calculateVisitFrequencyRole(customer, tenant);

                    return (
                        <div style={{
                            position: 'relative', zIndex: 10,
                            background: 'rgba(255,255,255,0.95)',
                            backdropFilter: 'blur(12px)',
                            borderRadius: '20px',
                            padding: '16px 18px',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '14px'
                        }}>
                            {/* Avatar */}
                            <button
                                onClick={() => setActiveTab('profile')}
                                style={{
                                    width: '52px', height: '52px', borderRadius: '16px',
                                    background: `linear-gradient(135deg, ${primaryColor}, ${tierColor})`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: 'none', padding: 0, cursor: 'pointer',
                                    color: 'white', flexShrink: 0,
                                    boxShadow: `0 4px 12px ${primaryColor}40`
                                }}
                            >
                                <User size={24} />
                            </button>

                            {/* Name & Badges */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <h1 style={{ fontSize: '1.1rem', fontWeight: 900, letterSpacing: '-0.3px', margin: 0, color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {customer.firstName ? `${customer.firstName} ${customer.lastName || ''}` : t('portal.welcomeUser', 'Müşteri')}
                                </h1>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                                    <div style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                                        fontSize: '0.6rem', fontWeight: 900, color: 'white',
                                        background: tierColor, padding: '3px 10px', borderRadius: '20px',
                                        textTransform: 'uppercase', letterSpacing: '0.5px',
                                        boxShadow: `0 2px 6px ${tierColor}40`
                                    }}>
                                        <Star size={9} fill="currentColor" strokeWidth={3} />
                                        {currentTier?.label || t('portal.member', 'Üye')}
                                    </div>
                                    {visitRole && (
                                        <div style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                            fontSize: '0.6rem', fontWeight: 800,
                                            color: visitRole.color || 'var(--text-muted)',
                                            background: `${visitRole.color || '#666'}12`,
                                            padding: '3px 10px', borderRadius: '20px',
                                            border: `1px solid ${visitRole.color || '#666'}25`
                                        }}>
                                            {visitRole.label}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Points */}
                            <button
                                onClick={() => setActiveTab('rewards')}
                                style={{
                                    background: `linear-gradient(135deg, ${primaryColor}08, ${primaryColor}15)`,
                                    padding: '10px 14px', borderRadius: '14px',
                                    border: `1px solid ${primaryColor}20`,
                                    cursor: 'pointer', display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', minWidth: '62px',
                                    transition: 'all 0.2s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                <span style={{ fontSize: '1.2rem', fontWeight: 900, color: primaryColor, lineHeight: 1 }}>
                                    {customer.points || 0}
                                </span>
                                <span style={{ fontSize: '0.55rem', fontWeight: 800, color: primaryColor, marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.7 }}>Puan</span>
                            </button>
                        </div>
                    );
                })()}
            </div>

            {/* Main Content Area */}
            <div style={{ maxWidth: '600px', margin: '2rem auto 0', padding: '0 1rem', position: 'relative', zIndex: 10 }}>
                {/* Floating Bottom Navigation */}
                <div style={{
                    position: 'fixed',
                    bottom: '1rem',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 'calc(100% - 2rem)',
                    maxWidth: '420px',
                    padding: '6px',
                    borderRadius: '20px',
                    background: 'rgba(255,255,255,0.92)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    display: 'flex',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    zIndex: 100,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
                    border: '1px solid rgba(0,0,0,0.06)'
                }}>
                    {[
                        { id: 'menu', icon: Utensils, label: t('portal.digitalMenu', 'Menü') },
                        ...(orderingEnabled ? [{ id: 'cart', icon: ShoppingCart, label: t('portal.cart', 'Sepet'), count: cart.reduce((sum, i) => sum + (i.quantity || 1), 0) }] : []),
                        { id: 'promotions', icon: Ticket, label: t('nav.promotions', 'İndirim') },
                        { id: 'rewards', icon: Gift, label: t('nav.rewards', 'Ödüller') },
                    ].map(tab => {
                        const isActive = activeTab === tab.id;
                        return (
                            <div
                                key={tab.id}
                                onClick={() => changeTab(tab.id)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: isActive ? '6px' : '0',
                                    color: isActive ? 'white' : '#9ca3af',
                                    padding: isActive ? '10px 18px' : '10px 14px',
                                    borderRadius: '16px',
                                    background: isActive ? primaryColor : 'transparent',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    boxShadow: isActive ? `0 4px 12px ${primaryColor}40` : 'none',
                                }}
                            >
                                <tab.icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                                {isActive && (
                                    <span style={{ fontSize: '0.7rem', fontWeight: 800, whiteSpace: 'nowrap' }}>{tab.label}</span>
                                )}

                                {tab.id === 'cart' && tab.count > 0 && !isActive && (
                                    <span style={{
                                        position: 'absolute',
                                        top: '4px',
                                        right: '8px',
                                        background: '#ef4444',
                                        color: 'white',
                                        fontSize: '0.55rem',
                                        minWidth: '16px',
                                        height: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '50%',
                                        fontWeight: 900,
                                    }}>
                                        {tab.count}
                                    </span>
                                )}
                                {tab.id === 'cart' && tab.count > 0 && isActive && (
                                    <span style={{
                                        background: 'rgba(255,255,255,0.3)',
                                        fontSize: '0.6rem',
                                        minWidth: '18px',
                                        height: '18px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '50%',
                                        fontWeight: 900,
                                        marginLeft: '2px'
                                    }}>
                                        {tab.count}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {activeTab === 'menu' && (
                    <DigitalMenu
                        menuData={menu}
                        inventory={inventory}
                        primaryColor={primaryColor}
                        onAddToCart={addToCart}
                        onUpdateQuantity={updateCartQuantity}
                        cart={cart}
                        cartCount={cart.length}
                        tenantPlan={orderingEnabled ? (tenant?.plan || 'pro') : 'free'}
                        tenant={tenant}
                    />
                )}

                {activeTab === 'cart' && orderingEnabled && (
                    <CustomerPortalCart
                        cart={cart}
                        tenant={tenant}
                        inventory={inventory}
                        customer={customer}
                        tableNumber={tableNumber}
                        setTableNumber={setTableNumber}
                        urlTableParam={urlTableParam}
                        allValidPromotions={allValidPromotions}
                        appliedPromotion={appliedPromotion}
                        setAppliedPromotion={(promo) => {
                            setAppliedPromotion(promo);
                            if (promo) setAppliedReward(null); // Mutual exclusivity: remove reward if promotion selected
                        }}
                        redemptions={redemptions}
                        isPromotionEligible={isPromotionEligible}
                        calculateCartTotal={calculateCartTotal}
                        calculateEarnedPoints={calculateEarnedPoints}
                        handlePayment={handlePayment}
                        handleOnlinePayment={handleOnlinePayment}
                        paymentLoading={paymentLoading}
                        primaryColor={primaryColor}
                        setActiveTab={setActiveTab}
                        updateCartQuantity={updateCartQuantity}
                        removeFromCart={removeFromCart}
                        rewardCode={rewardCode}
                        setRewardCode={setRewardCode}
                        appliedReward={appliedReward}
                        handleApplyReward={handleApplyReward}
                        activeRewardCodes={activeRewardCodes}
                        activeTab={activeTab}
                        addedCoupons={addedCoupons}
                    />
                )}


                {activeTab === 'rewards' && (
                    <CustomerPortalRewards
                        rewards={rewards}
                        customer={customer}
                        primaryColor={primaryColor}
                        handleRedeem={handleRedeem}
                        redeeming={redeeming}
                        activeRewardCodes={activeRewardCodes}
                    />
                )}

                {activeTab === 'promotions' && (
                    <CustomerPortalPromotions
                        campaigns={campaigns}
                        discounts={discounts}
                        primaryColor={primaryColor}
                        addedCoupons={addedCoupons}
                        setAddedCoupons={setAddedCoupons}
                    />
                )}

                {activeTab === 'profile' && (
                    <CustomerPortalProfile
                        customer={customer}
                        setCustomer={setCustomer}
                        isEditingProfile={isEditingProfile}
                        setIsEditingProfile={setIsEditingProfile}
                        handleUpdateProfile={handleUpdateProfile}
                        paymentLoading={paymentLoading}
                        tenant={tenant}
                        customerId={customerId}
                        primaryColor={primaryColor}
                        history={history}
                        discounts={discounts}
                    />
                )}

                {activeTab === 'notifications' && (
                    <CustomerPortalNotifications
                        filteredNotifications={filteredNotifications}
                        customer={customer}
                        customerId={customerId}
                        primaryColor={primaryColor}
                        openNotification={openNotification}
                        readNotifications={readNotifications}
                    />
                )}
            </div>

            {/* Redemption Modal */}
            {
                showCode && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(10px)' }}>
                        <div className="card-premium fade-in" style={{ maxWidth: '400px', width: '100%', textAlign: 'center', padding: '3rem' }}>
                            <div style={{ color: '#22c55e', marginBottom: '1.5rem' }}><CheckCircle2 size={64} style={{ margin: '0 auto' }} /></div>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: 900, marginBottom: '0.5rem' }}>{t('discounts.redeemCode')}</h2>
                            <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>{t('discounts.codeHint')}</p>

                            <div style={{
                                background: 'var(--bg-main)',
                                border: `2px dashed ${primaryColor}`,
                                padding: '2rem',
                                borderRadius: '20px',
                                fontSize: '2.5rem',
                                fontWeight: 900,
                                letterSpacing: '5px',
                                color: primaryColor,
                                marginBottom: '2rem'
                            }}>
                                {showCode}
                            </div>

                            <button
                                onClick={closeCodeModal}
                                style={{ width: '100%', padding: '12px', background: 'var(--text-main)', color: 'var(--bg-card)', fontWeight: 800, borderRadius: '12px', border: 'none' }}
                            >
                                {t('common.close')}
                            </button>
                        </div>
                    </div>
                )
            }

            {/* Notification Detail Modal */}
            {
                selectedNotification && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(10px)' }}>
                        <div className="card-premium fade-in" style={{ maxWidth: '450px', width: '100%', padding: 0, overflow: 'hidden' }}>
                            {selectedNotification.imageUrl && (
                                <div style={{ width: '100%', aspectRatio: '16/9', overflow: 'hidden' }}>
                                    <img src={selectedNotification.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                            )}
                            <div style={{ padding: '2rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                                    <div>
                                        <h2 style={{ fontSize: '1.5rem', fontWeight: 900, marginBottom: '0.25rem' }}>{selectedNotification.title}</h2>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{selectedNotification.createdAt ? new Date(selectedNotification.createdAt).toLocaleString() : ''}</div>
                                    </div>
                                    <button onClick={closeNotificationModal} style={{ background: 'var(--bg-main)', border: 'none', color: 'var(--text-muted)', padding: '8px', borderRadius: '10px' }}>
                                        <XCircle size={20} />
                                    </button>
                                </div>

                                <p style={{ color: 'var(--text-main)', lineHeight: 1.6, marginBottom: '2rem', whiteSpace: 'pre-wrap' }}>
                                    {selectedNotification.body}
                                </p>

                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <button
                                        onClick={closeNotificationModal}
                                        style={{ flex: 1, padding: '12px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-main)', fontWeight: 800, borderRadius: '12px' }}
                                    >
                                        {t('common.close')}
                                    </button>
                                    {selectedNotification.actionLabel && (
                                        <button
                                            onClick={() => {
                                                if (selectedNotification.actionLink) {
                                                    if (selectedNotification.actionLink.startsWith('http')) {
                                                        window.open(selectedNotification.actionLink, '_blank');
                                                    } else if (selectedNotification.actionLink.includes('/portal')) {
                                                        navigate(selectedNotification.actionLink);
                                                    } else {
                                                        // Internal tab navigation
                                                        const tab = selectedNotification.actionLink.split('/').pop();
                                                        if (['menu', 'rewards', 'promotions', 'profile', 'notifications', 'cart'].includes(tab)) {
                                                            setActiveTab(tab);
                                                        } else if (tab === 'overview' || tab === 'coupons') {
                                                            setActiveTab('menu');
                                                        }
                                                    }
                                                }
                                                setSelectedNotification(null);
                                            }}
                                            style={{ flex: 1, padding: '12px', background: primaryColor, color: 'var(--text-on-primary)', fontWeight: 800, borderRadius: '12px', border: 'none' }}
                                        >
                                            {selectedNotification.actionLabel}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Foreground Notification Toast */}
            {
                foregroundNotification && (
                    <div style={{
                        position: 'fixed',
                        top: '20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(255, 255, 255, 0.95)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                        padding: '1rem',
                        borderRadius: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        zIndex: 10000,
                        width: '90%',
                        maxWidth: '400px',
                        border: '1px solid var(--border)',
                        backdropFilter: 'blur(10px)',
                        animation: 'slideDown 0.3s ease-out'
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: 'var(--primary)',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            <Bell size={20} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 800, fontSize: '0.875rem' }}>{foregroundNotification.title}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{foregroundNotification.body}</div>
                        </div>
                        <button onClick={() => setForegroundNotification(null)} style={{ background: 'transparent', padding: '4px', border: 'none' }}>
                            <XCircle size={20} color="var(--text-muted)" />
                        </button>
                    </div>
                )
            }

            <style>{`
                @keyframes slideDown { from { transform: translate(-50%, -100%); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }
                .card-premium {
                    background: var(--bg-card);
                    padding: 1.5rem;
                    border-radius: 24px;
                    border: 1px solid var(--border);
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                }
                .cart-pulse {
                    animation: cart-pulse 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                @keyframes cart-pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.1); }
                    100% { transform: scale(1); }
                }
                .glass-premium {
                    background: linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%);
                    backdrop-filter: blur(20px);
                    border: 1px solid rgba(255,255,255,0.4);
                    border-radius: 32px;
                }
                .fade-in { animation: fadeIn 0.4s ease-out; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                .spinner {
                    width: 40px; height: 40px; border: 4px solid rgba(0,0,0,0.1); border-top-color: ${primaryColor}; border-radius: 50%; animation: spin 1s linear infinite;
                }
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes pulse-red {
                    0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
                    70% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
                }
            `}</style>

            {/* Payment Checkout Modal (iyzico / PayTR) */}
            {checkoutModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 99999, padding: '1rem'
                }}>
                    <div style={{
                        background: 'white', borderRadius: '20px',
                        width: '100%', maxWidth: '500px', maxHeight: '90vh',
                        overflow: 'hidden', display: 'flex', flexDirection: 'column',
                        boxShadow: '0 25px 60px rgba(0,0,0,0.2)'
                    }}>
                        <div style={{
                            padding: '1rem 1.25rem',
                            borderBottom: '1px solid #eee',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <CreditCard size={20} color="#1a1f71" />
                                <span style={{ fontWeight: 800, fontSize: '1rem' }}>Guvenli Odeme</span>
                            </div>
                            <button
                                onClick={() => setCheckoutModal(null)}
                                style={{
                                    background: '#f3f4f6', border: 'none', borderRadius: '10px',
                                    width: '32px', height: '32px', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer', fontSize: '18px', color: '#6b7280'
                                }}
                            >
                                &times;
                            </button>
                        </div>
                        {checkoutModal.provider === 'paytr' ? (
                            <iframe
                                src={checkoutModal.iframeUrl}
                                style={{ flex: 1, border: 'none', minHeight: '460px', width: '100%' }}
                                frameBorder="0"
                            />
                        ) : (
                            <div
                                style={{ flex: 1, overflow: 'auto', minHeight: '400px' }}
                                dangerouslySetInnerHTML={{ __html: checkoutModal.html }}
                            />
                        )}
                    </div>
                </div>
            )}
        </div >
    );
};

export default CustomerPortal;
